import { lazy, Suspense, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";

import Homepage from "./screens/homepage/Homepage";
import {
  DEFAULTS,
  OPERATIONS_MAINTENANCE,
  ROUTES,
} from "./constants/routesConstants";
import {
  CAP_X_STEPS_ORDER,
  LIFE_CYCLE_COST_STEPS_ORDER,
  ROUTES_TO_STEPS,
  STEPS,
} from "./constants/stepsConstants";
import {
  EVALUATION_TYPES,
  STEP_STORAGE_KEYS,
} from "./constants/storageConstants";
import { projectDataPropType } from "./util/propTypeHelper";
import CapacityAnalysis from "./screens/capx/CapacityAnalysis";
import { getEvaluationType } from "./util/dataSelectors";
import ICEToolNavBar from "./ui/ICEToolNavBar";
import GlobalSafetyData from "./screens/lifeCycleCost/globalSafetyData/GlobalSafetyData";
import SafetyDefaults from "./screens/lifeCycleCost/safety/SafetyDefaults";
import TrafficVolumesDefaults from "./screens/trafficVolumes/defaults/TrafficVolumesDefaults";
import OperationsAndMaintenanceDefaults from "./screens/lifeCycleCost/costParameters/OperationsAndMaintenanceDefaults";
import CostParametersDefaults from "./screens/lifeCycleCost/costParameters/CostParametersDefaults";
import PageSkeleton from "./ui/PageSkeleton";

const ProjectInformation = lazy(() =>
  import("./screens/projectInformation/ProjectInformation")
);
const About = lazy(() => import("./screens/homepage/About"));
const Contact = lazy(() => import("./screens/homepage/Contact"));
const Disclaimer = lazy(() => import("./screens/homepage/Disclaimer"));
const GlobalInputData = lazy(() =>
  import("./screens/lifeCycleCost/GlobalInputData")
);
const TrafficVolumes = lazy(() =>
  import("./screens/trafficVolumes/TrafficVolumes")
);
const DesignSelection = lazy(() =>
  import("./screens/designSelection/DesignSelection")
);
const Delay = lazy(() => import("./screens/lifeCycleCost/delay/Delay"));
const CostParameters = lazy(() =>
  import("./screens/lifeCycleCost/costParameters/CostParameters")
);
const Safety = lazy(() => import("./screens/lifeCycleCost/safety/Safety"));
const Outputs = lazy(() => import("./screens/lifeCycleCost/outputs/Outputs"));

export default function Routing({
  projectData,
  onNewProjectStart,
  saveFormData,
  setCurrentProject,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const initialActiveStep =
    ROUTES_TO_STEPS[location.pathname] || STEPS.HOMEPAGE;
  const [activeStep, setActiveStep] = useState(initialActiveStep);

  // update active step if someone clicks on a link to another page
  useEffect(() => {
    setActiveStep(ROUTES_TO_STEPS[location.pathname]);
  }, [setActiveStep, location]);

  /**
   * Move forward or backward one step
   * @param {string} [evaluationType] either CAP_X or LIFE_CYCLE_COST. Only passed in when changing from one evaluation type to another.
   * We pass it in since the "savedEvaluationType" is not updated yet when this function is called immediately after updating the value.
   * @param {boolean} [isBackClick] whether to go forward or backward one step
   */
  const changeSteps = ({ evaluationType, isBackClick = false } = {}) => {
    const currentEvaluationType =
      evaluationType ?? getEvaluationType(projectData);
    const STEPS_ORDER =
      currentEvaluationType === EVALUATION_TYPES.CAP_X
        ? CAP_X_STEPS_ORDER
        : LIFE_CYCLE_COST_STEPS_ORDER;
    const activeStepIndex = STEPS_ORDER.findIndex(
      (step) => step === activeStep
    );
    const newStep = STEPS_ORDER[activeStepIndex + (isBackClick ? -1 : 1)];
    setActiveStep(newStep);
    navigate(ROUTES[newStep]);
  };
  const navigateToNextStep = () => changeSteps();
  const navigateToPrevStep = () => changeSteps({ isBackClick: true });

  const commonProps = {
    handleMainButtonClick: navigateToNextStep,
    handleSecondaryButtonClick: navigateToPrevStep,
    projectData,
    saveFormData,
  };

  return (
    <>
      <ICEToolNavBar
        currentEvaluationType={getEvaluationType(projectData)}
        activeStep={activeStep}
      />
      <Suspense fallback={<PageSkeleton />}>
        <Routes>
          {/* 
        
            Shared routes 
          
        */}
          <Route
            path={ROUTES.HOMEPAGE}
            element={
              <Homepage
                handleNewAnalysisClick={() => {
                  onNewProjectStart();
                  navigateToNextStep();
                }}
                navigateToNextStep={navigateToNextStep}
                projectInfo={projectData[STEP_STORAGE_KEYS.PROJECT_INFORMATION]}
                saveFormData={saveFormData}
                setCurrentProject={setCurrentProject}
              />
            }
          />
          <Route
            path={ROUTES.PROJECT_INFORMATION}
            element={
              <ProjectInformation
                {...commonProps}
                handleMainButtonClick={({ evaluationType }) =>
                  changeSteps({ evaluationType, isBackClick: false })
                }
              />
            }
          />
          <Route path={ROUTES.TRAFFIC_VOLUMES}>
            <Route path="" element={<TrafficVolumes {...commonProps} />} />
            <Route
              path={DEFAULTS}
              element={<TrafficVolumesDefaults {...commonProps} />}
            />
          </Route>
          <Route
            path={ROUTES.DESIGN_SELECTION}
            element={<DesignSelection {...commonProps} />}
          />
          <Route path={ROUTES.DISCLAIMER} element={<Disclaimer />} />
          <Route path={ROUTES.ABOUT} element={<About />} />
          <Route path={ROUTES.CONTACT} element={<Contact />} />

          {/* 
        
            Life Cycle Cost routes 
          
        */}
          <Route
            path={ROUTES.GLOBAL_INPUT_DATA}
            element={<GlobalInputData {...commonProps} />}
          />
          <Route path={ROUTES.DELAY} element={<Delay {...commonProps} />} />
          <Route path={ROUTES.COST_PARAMETERS}>
            <Route path="" element={<CostParameters {...commonProps} />} />
            <Route
              path={DEFAULTS}
              element={<CostParametersDefaults {...commonProps} />}
            />
            <Route
              path={`${DEFAULTS}/${OPERATIONS_MAINTENANCE}`}
              element={<OperationsAndMaintenanceDefaults {...commonProps} />}
            />
          </Route>
          <Route
            path={ROUTES.GLOBAL_SAFETY_DATA}
            element={<GlobalSafetyData {...commonProps} />}
          />
          <Route path={ROUTES.SAFETY}>
            <Route path="" element={<Safety {...commonProps} />} />
            <Route
              path={DEFAULTS}
              element={<SafetyDefaults {...commonProps} />}
            />
          </Route>
          <Route path={ROUTES.OUTPUTS} element={<Outputs {...commonProps} />} />

          {/* 
        
            Cap X routes 
          
        */}
          <Route
            path={ROUTES.CAPACITY_ANALYSIS}
            element={<CapacityAnalysis {...commonProps} />}
          />
        </Routes>
      </Suspense>
    </>
  );
}

Routing.propTypes = {
  projectData: projectDataPropType.isRequired,
  onNewProjectStart: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired,
  setCurrentProject: PropTypes.func.isRequired,
};
